/**
 * BillingTab - the Super Admin invoicing queue.
 *
 * The office's working surface for the billing tail of the work order
 * lifecycle: completed -> invoiced -> closed. Field crews finish the work and
 * a manager approves it; raising the invoice is an accounting act, so this tab
 * — and the transitions it drives — are super-admin only, matching the gate in
 * WorkOrderController and the empty allowedRoles in utils/statusTransitions.
 *
 * Three stages, one per sub-tab:
 *   - Ready to Invoice — completed AND billable. The work list.
 *   - Invoiced         — billed, not yet closed out. The confirmation view.
 *   - Closed           — done with. Kept reachable because "did we bill that?"
 *                        is asked long after the fact.
 *
 * Every filter composes with the stage rather than replacing it, so the queue
 * can be narrowed to one customer, one business line, one billing
 * classification, or a completion-date window and still be a queue. That
 * matters for a monthly invoice run against a single customer, which is how
 * this actually gets used.
 *
 * Selection + bulk invoicing live on the Ready stage only. Rows that cannot be
 * billed yet — an hours-tracked job whose hours nobody has approved — are
 * shown, labelled, and made unselectable rather than hidden: the office needs
 * to see what is stuck and go chase it, not wonder why a job never appeared.
 *
 * Atomic Design: the queue is assembled from the shared pieces rather than
 * hand-rolled. SearchBar + FilterBar own the filter strip, DataTable owns the
 * table (and with it the phone card layout, the loading skeleton, and the
 * empty state), Checkbox owns the selection boxes, Pagination owns the pager.
 * This tab's job is to decide what goes in them.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { camelizeKeys } from '../../services/api';
import { companyService } from '../../services/companyService';
import { businessLineService } from '../../services/businessLineService';
import { workOrderService, normalizeWorkOrder } from '../../services/workOrderService';
import type { WorkOrder, PaginatedResponse, BulkInvoiceResult } from '../../services/workOrderService';
import { qk } from '../../lib/queryKeys';
import { BILLING_INFO_TAGS } from '../../utils/constants';
import { Alert, Button, Checkbox } from '../../components/atoms';
import { TabBar, Pagination, SearchBar, FilterBar } from '../../components/molecules';
import type { FilterConfig } from '../../components/molecules';
/* Imported from the component's own folder rather than the organisms barrel:
   the barrel pulls the map and calendar graphs in with it, which a billing
   queue has no use for. */
import { DataTable } from '../../components/organisms/DataTable';
import { ResponsiveFilters } from '../../components/organisms/ResponsiveFilters';
import type { DataTableColumn } from '../../components/organisms/DataTable';
import { formatStatusLabel } from '../../utils/formatLabel';
import { formatDate } from '../../utils/formatDate';
import { formatCurrencyOrNull } from '../../utils/formatCurrency';

/* ============================================
 * STAGES
 * ============================================ */

/** The three points a work order can sit at in the billing tail. */
type BillingStage = 'ready' | 'invoiced' | 'closed';

const STAGE_TABS: { key: BillingStage; label: string }[] = [
	{ key: 'ready', label: 'Ready to Invoice' },
	{ key: 'invoiced', label: 'Invoiced' },
	{ key: 'closed', label: 'Closed' },
];

/**
 * Query params each stage contributes, before the user's own filters are
 * merged in. 'ready' is the only one that constrains billability — once a work
 * order has been invoiced, whether it was tagged billable is history, and
 * re-applying the filter would hide rows the office just billed.
 */
const STAGE_PARAMS: Record<BillingStage, Record<string, string>> = {
	ready: { status: 'completed', billable: '1' },
	invoiced: { status: 'invoiced' },
	closed: { status: 'closed' },
};

const STAGE_EMPTY_COPY: Record<BillingStage, string> = {
	ready: 'Nothing is waiting to be invoiced.',
	invoiced: 'No invoiced work orders.',
	closed: 'No closed work orders.',
};

/* Minimal shape of the /companies rows this tab needs for its filter. */
interface CompanyOption { id: string; name: string }
/* Minimal shape of the CMS-managed business lines used by the filter. */
interface BusinessLineOption { id: number; slug: string; label: string }

/**
 * The filter keys, as the FilterBar values object holds them.
 *
 * They are the API's own param names so the query builder can spread them
 * straight through instead of translating between two vocabularies.
 */
const FILTER_KEYS = ['company_id', 'business_line', 'billing_type', 'completed_from', 'completed_to'] as const;
type FilterKey = typeof FILTER_KEYS[number];
type FilterValues = Record<FilterKey, string>;

const EMPTY_FILTERS: FilterValues = {
	company_id: '',
	business_line: '',
	billing_type: '',
	completed_from: '',
	completed_to: '',
};

/* ============================================
 * HELPERS
 * ============================================ */

/**
 * Why a work order can't be billed right now, or null when it can.
 *
 * Mirrors the server's eligibility rules (WorkOrder::readyToInvoice) so the
 * row explains itself before the user selects it, instead of the batch coming
 * back with a skip reason they then have to match up to a row.
 */
function blockedReason(wo: WorkOrder): string | null {
	if (wo.requiresHours && !wo.hoursApprovedAt) return 'Hours not approved';
	return null;
}

/* ============================================
 * COMPONENT
 * ============================================ */

const BillingTab: React.FC = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	/* ---- Stage + filters ---- */
	const [stage, setStage] = useState<BillingStage>('ready');
	const [search, setSearch] = useState('');
	const [filters, setFilters] = useState<FilterValues>(EMPTY_FILTERS);
	const [page, setPage] = useState(1);

	/* ---- Selection ---- */
	/* Ids ticked for the next bulk invoice. Kept as a Set of string ids so the
	   header checkbox can diff against the page's selectable rows cheaply. */
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	/* Result of the last bulk run — surfaced as a banner until dismissed or
	   superseded, because partial success is the normal outcome and the user
	   has to be told which rows did not go through. */
	const [batchNotice, setBatchNotice] = useState<{ variant: 'success' | 'warning'; message: string } | null>(null);

	/* Changing the stage or any filter invalidates both the page number and
	   the selection: a ticked row that has scrolled out of the result set
	   would otherwise be billed invisibly on the next submit. */
	useEffect(() => {
		setPage(1);
		setSelectedIds(new Set());
	}, [stage, search, filters]);

	/* ---- Filter option lookups ---- */
	/* per_page=999 so the customer dropdown isn't silently truncated by
	   pagination — the same one-shot-lookup pattern AdminPage uses. */
	const companiesQuery = useQuery<CompanyOption[]>({
		queryKey: qk.companies.list({ per_page: '999' }),
		queryFn: async () => {
			const res = await companyService.list({ per_page: '999' });
			const rows: any[] = Array.isArray(res.data) ? res.data : ((res.data as any)?.data ?? []);
			/* Company PK is company_id; mirror it to id so option values
			   aren't the literal string "undefined". */
			return rows.map((c) => ({ id: String(c.id ?? c.company_id), name: c.name }));
		},
	});

	/* active=1: the filter only needs lines someone could still pick. The CMS
	   is where disabled ones get managed. */
	const businessLinesQuery = useQuery<BusinessLineOption[]>({
		queryKey: qk.businessLines.list({ active: '1' }),
		queryFn: async () => {
			const res = await businessLineService.list(true);
			const rows: any[] = Array.isArray(res.data) ? res.data : ((res.data as any)?.data ?? []);
			return (camelizeKeys(rows) as any[]).map((bl) => ({ id: bl.id, slug: bl.slug, label: bl.label }));
		},
	});

	/* Annotated rather than inferred: useQuery resolves to `any` in this
	   project, so every derived value has to name its own type — the same
	   thing WorkOrdersPage and useWorkOrderWorkflow do. */
	const companyOptions: CompanyOption[] = useMemo(() => companiesQuery.data ?? [], [companiesQuery.data]);
	const businessLineOptions: BusinessLineOption[] = useMemo(() => businessLinesQuery.data ?? [], [businessLinesQuery.data]);

	/* ---- Filter strip config ---- */
	/* FilterBar renders "All {label}" as each dropdown's placeholder, so the
	   labels are plural on purpose. Dates are labelled by what they bound —
	   an invoice run is scoped by when the work FINISHED, not when the ticket
	   was opened. */
	const filterConfigs: FilterConfig[] = useMemo(() => [
		{
			key: 'company_id',
			label: 'Customers',
			type: 'select',
			options: companyOptions.map((c) => ({ value: c.id, label: c.name })),
		},
		{
			key: 'business_line',
			label: 'Business Lines',
			type: 'select',
			options: businessLineOptions.map((bl) => ({ value: bl.slug, label: bl.label })),
		},
		{
			key: 'billing_type',
			label: 'Billing Tags',
			type: 'select',
			options: BILLING_INFO_TAGS.map((tag) => ({ value: tag, label: tag })),
		},
		{ key: 'completed_from', label: 'Completed From', type: 'date' },
		{ key: 'completed_to', label: 'Completed To', type: 'date' },
	], [companyOptions, businessLineOptions]);

	/* ---- The queue itself ---- */
	const params = useMemo<Record<string, string>>(() => {
		const p: Record<string, string> = { ...STAGE_PARAMS[stage], page: String(page) };
		if (search.trim()) p.search = search.trim();
		/* Only non-empty filters reach the API — an empty string would be a
		   real (and unmatchable) value on the server side. */
		for (const key of FILTER_KEYS) {
			if (filters[key]) p[key] = filters[key];
		}
		return p;
	}, [stage, page, search, filters]);

	const queueQuery = useQuery<{ items: WorkOrder[]; totalPages: number; totalItems: number }>({
		queryKey: qk.workOrders.list({ billing: stage, ...params }),
		queryFn: async () => {
			const res = await workOrderService.getAll(params);
			const data = res.data as PaginatedResponse<WorkOrder>;
			/* The list endpoint is paginated, but tolerate a flat array too —
			   every other caller in the app hedges the same way. */
			const rows: unknown[] = Array.isArray(data?.data)
				? data.data
				: (Array.isArray(res.data) ? (res.data as unknown[]) : []);
			return {
				items: (camelizeKeys(rows) as Record<string, unknown>[]).map(normalizeWorkOrder),
				totalPages: data?.last_page ?? 1,
				totalItems: data?.total ?? rows.length,
			};
		},
	});

	const workOrders: WorkOrder[] = useMemo(() => queueQuery.data?.items ?? [], [queueQuery.data]);
	const totalPages: number = queueQuery.data?.totalPages ?? 1;
	const totalItems: number = queueQuery.data?.totalItems ?? 0;

	/* Rows on this page that can actually be billed — the only ones the
	   checkboxes and the header select-all operate on. */
	const selectableIds = useMemo(
		() => workOrders.filter((wo) => blockedReason(wo) === null).map((wo) => String(wo.id)),
		[workOrders],
	);
	const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

	const toggleRow = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id); else next.add(id);
			return next;
		});
	};

	const toggleAll = () => {
		setSelectedIds(allSelected ? new Set() : new Set(selectableIds));
	};

	/* ---- Bulk invoice ---- */
	const invoiceMutation = useMutation({
		mutationFn: (ids: string[]) => workOrderService.bulkInvoice(ids),
		onSuccess: (res: { data: BulkInvoiceResult }) => {
			const { invoiced, skipped } = res.data;
			/* Report both halves. A silent "done" after a partial run is how an
			   unbilled job quietly stays unbilled. */
			if (skipped.length === 0) {
				setBatchNotice({
					variant: 'success',
					message: `Marked ${invoiced.length} work order${invoiced.length === 1 ? '' : 's'} as invoiced.`,
				});
			} else {
				const detail = skipped.map((s) => `WO-${s.id}: ${s.reason}`).join(' · ');
				setBatchNotice({
					variant: 'warning',
					message: `Invoiced ${invoiced.length} of ${invoiced.length + skipped.length}. Skipped — ${detail}`,
				});
			}
			setSelectedIds(new Set());
			/* Every work order list is now stale, not just this queue: the
			   rows moved from completed to invoiced. */
			queryClient.invalidateQueries({ queryKey: qk.workOrders.all });
		},
		onError: (err) => {
			setBatchNotice({
				variant: 'warning',
				message: err instanceof Error ? err.message : 'Failed to mark the selected work orders as invoiced.',
			});
		},
	});

	const handleBulkInvoice = () => {
		if (selectedIds.size === 0) return;
		setBatchNotice(null);
		invoiceMutation.mutate(Array.from(selectedIds));
	};

	const hasFilters = Boolean(search) || FILTER_KEYS.some((key) => filters[key]);
	const isLoading = queueQuery.isLoading;
	const error = queueQuery.error ? (queueQuery.error as Error).message : null;
	/* Selection only makes sense where there is something to do with it. */
	const selectable = stage === 'ready';

	/* ---- Columns ---- */
	/* Built here rather than at module scope because the selection column
	   closes over the current selection, and the whole set is rebuilt when the
	   stage flips selection on or off. */
	const columns: DataTableColumn<WorkOrder>[] = useMemo(() => {
		const selectColumn: DataTableColumn<WorkOrder> = {
			key: 'select',
			/* Header select-all covers the billable rows on THIS page only —
			   it never reaches across pagination, which would tick rows the
			   user has not seen. */
			header: (
				<Checkbox
					checked={allSelected}
					onChange={toggleAll}
					disabled={selectableIds.length === 0}
					hitArea="inset"
					ariaLabel="Select all billable work orders on this page"
					className="billing-tab__select-all"
					dataRef="organism-billing-tab-select-all"
				/>
			),
			headerClassName: 'w-44px',
			cellClassName: 'w-44px',
			render: (wo) => {
				const id = String(wo.id);
				return (
					<Checkbox
						checked={selectedIds.has(id)}
						onChange={() => toggleRow(id)}
						disabled={blockedReason(wo) !== null}
						hitArea="inset"
						ariaLabel={`Select WO-${id}`}
						className="billing-tab__select-row"
						dataRef="organism-billing-tab-select-row"
					/>
				);
			},
		};

		const dataColumns: DataTableColumn<WorkOrder>[] = [
			{
				key: 'order',
				header: 'Order',
				render: (wo) => (
					/* An inline record link, so it is exempt from the 44px
					   floor — forcing a box on it would break the line box.
					   See CONVENTIONS.md §5. */
					<button type="button" onClick={() => navigate(`/work-orders/${wo.id}`)} className="billing-tab__order-link text-14px font-semibold text-brand-accent hover:underline">
						WO-{wo.id}
					</button>
				),
			},
			{
				key: 'project',
				header: 'Project / Customer',
				render: (wo) => {
					const blocked = blockedReason(wo);
					return (
						<div className="billing-tab__project">
							<div className="billing-tab__project-name font-medium text-brand-text">{wo.projectName}</div>
							<div className="billing-tab__customer text-12px text-brand-text-muted">{wo.companyName || '—'}</div>
							{/* Say why a row can't be ticked, right where the user
							    is looking for the checkbox that isn't working. */}
							{blocked && <div className="billing-tab__blocked mt-2px text-12px text-amber-700">{blocked}</div>}
						</div>
					);
				},
			},
			{
				key: 'business_line',
				header: 'Business Line',
				headerClassName: 'hidden tablet:table-cell',
				cellClassName: 'hidden tablet:table-cell text-brand-text-muted',
				render: (wo) => (
					<span className="billing-tab__business-line">
						{wo.businessLine ? formatStatusLabel(wo.businessLine) : '—'}
						{wo.subType && <span className="block text-12px">{formatStatusLabel(wo.subType)}</span>}
					</span>
				),
			},
			{
				key: 'billing',
				header: 'Billing',
				headerClassName: 'hidden tablet:table-cell',
				cellClassName: 'hidden tablet:table-cell text-brand-text-muted',
				render: (wo) => (wo.billingInfo ?? []).length > 0 ? (wo.billingInfo ?? []).join(', ') : '—',
			},
			{
				key: 'completed',
				header: 'Completed',
				cellClassName: 'text-brand-text-muted',
				render: (wo) => formatDate(wo.completionDate),
			},
			{
				key: 'amount',
				header: 'Amount',
				headerClassName: 'text-right',
				cellClassName: 'text-right',
				/* An em dash rather than "$0.00" when there is no total:
				   unpriced is not the same as worthless, and the office chases
				   the two differently. */
				render: (wo) => formatCurrencyOrNull(wo.totalBillablePostTax) ?? '—',
			},
		];

		return selectable ? [selectColumn, ...dataColumns] : dataColumns;
	}, [selectable, selectedIds, selectableIds, allSelected, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<div className="billing-tab" data-ref="organism-billing-tab">
			{/* Stage sub-tabs */}
			<TabBar
				className="billing-tab__stages"
				tabs={STAGE_TABS}
				activeKey={stage}
				onChange={(key) => setStage(key as BillingStage)}
			/>

			{/* Filter strip. Every control here ANDs with the stage above, so
			    narrowing to one customer still leaves a workable queue. */}
			<div className="billing-tab__filters mb-16px">
				<SearchBar value={search} onChange={setSearch} placeholder="Search work orders..." className="billing-tab__search mb-12px" />
				{/* Customers, lines, tags and dates fold into a Filters pop-up
				    below desktop so the queue keeps the width; FilterBar's own
				    Clear resets them. */}
				<ResponsiveFilters title="Filter billing" activeCount={FILTER_KEYS.filter((key) => filters[key]).length} className="billing-tab__filter-bar" dataRef="page-billing-tab-filters">
					<FilterBar
						filters={filterConfigs}
						values={filters}
						onChange={(next) => setFilters({ ...EMPTY_FILTERS, ...next } as FilterValues)}
					/>
				</ResponsiveFilters>
			</div>

			{/* Outcome of the last bulk run */}
			{batchNotice && (
				<Alert variant={batchNotice.variant === 'success' ? 'success' : 'warning'} className="billing-tab__batch-notice mb-16px" onDismiss={() => setBatchNotice(null)}>
					{batchNotice.message}
				</Alert>
			)}

			{error && <Alert variant="error" className="billing-tab__error mb-16px">{error}</Alert>}

			{/* Selection summary + the action itself. Present on the Ready stage
			    whenever there is anything to bill, so the button doesn't appear
			    and disappear under the user's cursor as rows are ticked. */}
			{selectable && !isLoading && workOrders.length > 0 && (
				<div className="billing-tab__selection flex flex-wrap items-center justify-between gap-8px mb-12px">
					<p className="billing-tab__selection-count text-13px text-brand-text-muted">
						{selectedIds.size > 0
							? `${selectedIds.size} selected`
							: `${totalItems} work order${totalItems === 1 ? '' : 's'} ready to invoice`}
					</p>
					<Button
						variant="primary"
						size="sm"
						onClick={handleBulkInvoice}
						disabled={selectedIds.size === 0 || invoiceMutation.isPending}
					>
						{invoiceMutation.isPending ? 'Marking…' : `Mark Invoiced${selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}`}
					</Button>
				</div>
			)}

			{/* The queue. DataTable owns the desktop table, the phone card
			    layout, the loading skeleton, and the empty state — which is why
			    only the empty COPY is decided here. */}
			<DataTable
				className="billing-tab__queue"
				columns={columns}
				data={workOrders}
				keyExtractor={(wo) => String(wo.id)}
				isLoading={isLoading}
				emptyMessage={hasFilters ? 'No work orders match these filters.' : STAGE_EMPTY_COPY[stage]}
			/>

			{totalPages > 1 && (
				<Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} className="billing-tab__pagination mt-16px" />
			)}
		</div>
	);
};

export default BillingTab;
